import React, {
    ReactElement,
} from "react";
import { Form } from "react-bootstrap";
import { BiTrash } from "react-icons/bi";
import { createId, Json } from "./base";
import ExpandingSection from "../../../../components/expanding-section/ExpandingSection";
import InputList from "./Input";
import Template, { MapParameters, NamedParameters } from "./Template";
import Select from "../../../../components/select/Select";
import SerializationSelect from "./Serialization";
import WorkflowServiceApiSelector from "./ApiSelector";

const GRANT_TYPES = [
    {
        label: "Authorization Code",
        value: "authorization_code",
    },
    {
        label: "Refresh Token",
        value: "refresh_token",
    },
    {
        label: "Client Credentials",
        value: "client_credentials",
    },

];

export default function WorkflowServiceAuth({ json, onChange, rootJson }: {
    json: Json;
    onChange: (current: ((json: Json) => Json)) => void;
    rootJson: Json
}): ReactElement {
    const connection = json.connection as Json;
    const changeConnection = (child: Json) => {
        onChange(current => ({
            ...current,
            connection: {
                ...connection,
                ...child,
            },
        }));
    };
    const deleteKey = (key: string) => {
        onChange(current => {
            const copy = { ...current };
            delete (copy.connection as Record<string, string>)[key];
            return copy;
        });
    };

    if (connection?.oauth) {
        const oauth = connection?.oauth as Json | undefined;
        const changeOauth = (child: Json) => {
            changeConnection({
                oauth: {
                    ...oauth,
                    ...child,
                },
            });
        };
        return (
            <div>
                <div
                    className="d-flex justify-content-between align-items-start"
                >
                    <h5>OAuth Config</h5>
                    <button
                        type="button"
                        className="btn btn-none btn-sm p-0 text-light  "
                        onClick={() => deleteKey("oauth")}
                    >
                        <BiTrash />
                    </button>

                </div>

                <ExpandingSection title="User Facing">
                    <Form.Group className="mb-5">
                        <Form.Label className="fw-bolder">Instructions</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Please click Authorize to continue"
                            required
                            as="textarea"
                            onChange={evt => changeOauth({ instructions: evt.target.value })}
                            value={oauth?.instructions as string ?? ""}
                        />
                        <Form.Text>
                            Instructions shown to the end users
                        </Form.Text>
                    </Form.Group>

                    <h5>Input</h5>

                    <InputList
                        json={oauth?.input as Json[] ?? []}
                        rootJson={rootJson}
                        onChange={value => changeOauth({
                            input: value,
                        })}
                    />

                </ExpandingSection>
                <ExpandingSection title="Parameters">

                    <Form.Group className="mb-5">
                        <Form.Label className="fw-bolder">Client Id</Form.Label>
                        <Form.Text className="d-block">
                            Client Id
                        </Form.Text>
                        <Template
                            json={oauth?.clientId as Record<string, undefined>}
                            onChange={value => changeOauth({ clientId: value })}
                        />

                    </Form.Group>
                    <Form.Group className="mb-5">
                        <Form.Label className="fw-bolder">Client Secret</Form.Label>
                        <Form.Text className="d-block">
                            Client Secret
                        </Form.Text>
                        <Template
                            json={oauth?.clientSecret as Record<string, undefined>}
                            onChange={value => changeOauth({ clientSecret: value })}
                        />

                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Scopes</Form.Label>

                        <Form.Control
                            type="text"
                            placeholder={"Read\nWrite"}
                            required
                            as="textarea"
                            onChange={evt => changeOauth({ scopes: evt.target.value?.split("\n") })}
                            value={(oauth?.scopes as string[])?.join("\n")}
                        />
                        <Form.Text className="d-block">
                            OAuth Scopes
                        </Form.Text>

                    </Form.Group>

                </ExpandingSection>
                <ExpandingSection title="Redirect">

                    <Form.Group className="mb-5">
                        <Form.Label className="fw-bolder">Url</Form.Label>
                        <Form.Text className="d-block">
                            Redirect Url
                        </Form.Text>
                        <Template
                            json={(oauth?.redirect as Json)?.url as Json}
                            onChange={value => changeOauth({
                                redirect: {
                                    ...(oauth?.redirect as Json),
                                    url: value,
                                },
                            })}

                        />
                    </Form.Group>
                    <NamedParameters
                        json={(oauth?.redirect as Json)?.parameters as Json[]}
                        onChange={value => {
                            changeOauth({
                                redirect: {
                                    ...(oauth?.redirect as Json),
                                    parameters: value,
                                },
                            });
                        }}
                    />
                </ExpandingSection>
                <ExpandingSection title="Token">

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Url</Form.Label>
                        <Form.Text className="d-block">
                            Token Url
                        </Form.Text>
                        <Template
                            json={(oauth?.token as Json)?.url as Json}
                            onChange={value => changeOauth({
                                token: {
                                    ...(oauth?.token as Json),
                                    url: value,
                                },
                            })}

                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Grant Type</Form.Label>

                        <Select
                            className="mb-2"
                            onChange={value => changeOauth({
                                token: {
                                    ...(oauth?.token as Json),
                                    grantType: value.value,
                                },
                            })}
                            value={GRANT_TYPES.find(type => type.value === (oauth?.token as Json)?.grantType)}
                            options={GRANT_TYPES}
                        />
                        <Form.Text>
                            Grant type parameter
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Serialization</Form.Label>

                        <SerializationSelect
                            value={(oauth?.token as Json)?.serialization as string}
                            onChange={value => {
                                const newBody = {
                                    token: {
                                        ...(oauth?.token as Json),
                                    },
                                };
                                if (value) {
                                    newBody.token.serialization = value;
                                } else {
                                    delete newBody.token.serialization;
                                }
                                changeOauth(newBody);
                            }}
                        />
                        <Form.Text>
                            Serialization type
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-5">
                        <Form.Label className="fw-bolder">Reponse Serialization</Form.Label>

                        <SerializationSelect
                            value={(oauth?.token as Json)?.responseSerialization as string}
                            onChange={value => {
                                const newBody = {
                                    token: {
                                        ...(oauth?.token as Json),
                                    },
                                };
                                if (value) {
                                    newBody.token.responseSerialization = value;
                                } else {
                                    delete newBody.token.responseSerialization;
                                }
                                changeOauth(newBody);
                            }}
                        />
                        <Form.Text>
                            Response Serialization type
                        </Form.Text>
                    </Form.Group>

                    <MapParameters
                        title="Headers"
                        json={(oauth?.token as Json)?.headers as Json}
                        onChange={value => {
                            changeOauth({
                                token: {
                                    ...(oauth?.token as Json),
                                    headers: value,
                                },
                            });
                        }}
                    />
                </ExpandingSection>
                <ExpandingSection title="Refresh Token">

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Url</Form.Label>
                        <Form.Text className="d-block">
                            Token Url
                        </Form.Text>
                        <Template
                            json={(oauth?.refreshToken as Json)?.url as Json}
                            onChange={value => changeOauth({
                                refreshToken: {
                                    ...(oauth?.refreshToken as Json),
                                    url: value,
                                },
                            })}

                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Grant Type</Form.Label>

                        <Select
                            className="mb-2"
                            onChange={value => changeOauth({
                                refreshToken: {
                                    ...(oauth?.refreshToken as Json),
                                    grantType: value.value,
                                },
                            })}
                            value={GRANT_TYPES.find(type => type.value === (oauth?.refreshToken as Json)?.grantType)}
                            options={GRANT_TYPES}
                        />
                        <Form.Text>
                            Grant type parameter
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Serialization</Form.Label>

                        <SerializationSelect
                            value={(oauth?.refreshToken as Json)?.serialization as string}
                            onChange={value => {
                                const newBody = {
                                    refreshToken: {
                                        ...(oauth?.refreshToken as Json),
                                    },
                                };
                                if (value) {
                                    newBody.refreshToken.serialization = value;
                                } else {
                                    delete newBody.refreshToken.serialization;
                                }
                                changeOauth(newBody);
                            }}
                        />
                        <Form.Text>
                            Serialization type
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-5">
                        <Form.Label className="fw-bolder">Reponse Serialization</Form.Label>

                        <SerializationSelect
                            value={(oauth?.refreshToken as Json)?.responseSerialization as string}
                            onChange={value => {
                                const newBody = {
                                    refreshToken: {
                                        ...(oauth?.refreshToken as Json),
                                    },
                                };
                                if (value) {
                                    newBody.refreshToken.responseSerialization = value;
                                } else {
                                    delete newBody.refreshToken.responseSerialization;
                                }
                                changeOauth(newBody);
                            }}
                        />
                        <Form.Text>
                            Response Serialization type
                        </Form.Text>
                    </Form.Group>

                    <MapParameters
                        title="Headers"
                        json={(oauth?.refreshToken as Json)?.headers as Json}
                        onChange={value => {
                            changeOauth({
                                refreshToken: {
                                    ...(oauth?.refreshToken as Json),
                                    headers: value,
                                },
                            });
                        }}
                    />
                </ExpandingSection>
                <ExpandingSection title="Connection">

                    <Form.Group className="mb-5">
                        <Form.Label className="fw-bolder">Url</Form.Label>
                        <Template
                            json={(oauth?.connection as Json)?.url as Json}
                            onChange={value => changeOauth({
                                connection: {
                                    ...(oauth?.connection as Json),
                                    url: value,
                                },
                            })}

                        />
                        <Form.Text className="d-block">
                            This helps set up a default url that applies to all downstream requests.
                        </Form.Text>
                    </Form.Group>

                    <MapParameters
                        title="Headers"
                        json={(oauth?.connection as Json)?.headers as Json}
                        onChange={value => {
                            changeOauth({
                                connection: {
                                    ...(oauth?.connection as Json),
                                    headers: value,
                                },
                            });
                        }}
                    />
                </ExpandingSection>
                <ExpandingSection title="Events">

                    <ExpandingSection title="External ID Template">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">External ID Template</Form.Label>
                            <Form.Text className="d-block">
                                External ID Template
                            </Form.Text>
                            <WorkflowServiceApiSelector
                                rootJson={rootJson}
                                json={json?.externalIdTemplate as Json}
                                onChange={value => {
                                    changeOauth({
                                        externalIdTemplate: value,
                                    });
                                }}
                            />
                        </Form.Group>
                    </ExpandingSection>

                    <ExpandingSection title="After OAuth Handshake">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">After OAuth Handshake</Form.Label>
                            <Form.Text className="d-block">
                                After OAuth Handshake
                            </Form.Text>
                            <WorkflowServiceApiSelector
                                rootJson={rootJson}
                                json={json?.onSuccess as Json}
                                onChange={value => {
                                    changeOauth({
                                        onSuccess: value,
                                    });
                                }}

                            />
                        </Form.Group>

                    </ExpandingSection>
                </ExpandingSection>
                <ExpandingSection title="Variables">

                    <MapParameters
                        title="Variables"
                        json={oauth?.variables as Json}
                        onChange={value => {
                            changeOauth({
                                variables: value,
                            });
                        }}
                    />
                </ExpandingSection>

            </div>
        );
    }

    return (
        <button
            type="button"
            className="btn btn-primary py-1 px-2"
            onClick={() => changeConnection({
                id: `urn:fq:workflow-service-connection:(urn:fq:workflow-service:${json.id},${createId()})`,
                ...connection,
                oauth: {
                },
            })}
        >
            OAuth
        </button>
    );
}
